import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { TransactionKindCounts } from '@ankor-io/common/types/lighthouse'
import { formatCompactNumber } from '@ankor-io/common/util/numbers'
import { Heading, Loading, Text } from '@ankor-io/ui-components'

import { useTransactionsStatsQuery } from '@/queries'

type KindKey = keyof TransactionKindCounts

const KINDS: { key: KindKey; label: string; colour: string }[] = [
  { key: 'cardTransactions', label: 'Card transactions', colour: '#3b82f6' },
  { key: 'deposits', label: 'Deposits', colour: '#22c55e' },
  { key: 'payouts', label: 'Payouts', colour: '#f97316' },
  { key: 'conversions', label: 'Conversions', colour: '#eab308' },
  { key: 'walletTransfers', label: 'Wallet transfers', colour: '#a855f7' },
]

type TrendPoint = TransactionKindCounts & { date: string }

/** Convert a UTC YYYY-MM-DD trend date into a local-time label for the chart axis. */
function toLocalLabel(utcDate: string): string {
  const date = new Date(`${utcDate}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return utcDate
  return date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}

const TrendChartTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ value: number; name: string; stroke: string }>
  label?: string
}) => {
  if (!active || !payload || payload.length === 0) return null

  return (
    <div
      role="tooltip"
      className="min-w-44 rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-lg dark:border-gray-600 dark:bg-gray-800"
    >
      <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
      <dl className="mt-2 space-y-1">
        {payload.map((entry) => (
          <div
            key={entry.name}
            className="flex items-baseline justify-between gap-4 text-sm"
          >
            <dt style={{ color: entry.stroke }}>{entry.name}</dt>
            <dd
              className="tabular-nums font-medium"
              style={{ color: entry.stroke }}
            >
              {formatCompactNumber(entry.value)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

const KindStatBox = ({
  kind,
  totals,
  today,
}: {
  kind: (typeof KINDS)[number]
  totals: TransactionKindCounts
  today: TransactionKindCounts
}) => (
  <div
    key={kind.key}
    className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
  >
    <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
      {kind.label}
    </p>
    <p className="mt-1 text-2xl font-bold tabular-nums sm:text-3xl">{formatCompactNumber(totals[kind.key])}</p>
    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">today: {formatCompactNumber(today[kind.key])}</p>
  </div>
)

export const TransactionsStats = () => {
  const { data, isPending, isError, error } = useTransactionsStatsQuery()

  if (isPending) {
    return (
      <div data-testid="transactions-stats-loading">
        <Loading variant="spinner" />
      </div>
    )
  }

  if (isError) {
    return (
      <Text
        className="text-red-600 dark:text-red-400"
        data-testid="transactions-stats-error"
      >
        Unable to load transactions stats: {error.message}
      </Text>
    )
  }

  const chartData: TrendPoint[] = (data.trend ?? []).map((day) => ({
    ...day,
    date: toLocalLabel(day.date),
  }))

  return (
    <section
      aria-labelledby="transactions-stats-heading"
      className="space-y-3"
    >
      <Heading level={2}>Transactions stats</Heading>
      <Text className="text-gray-600 dark:text-gray-400">
        Successful transactions by type — all-time and today, with the last 7 days as a trend.
      </Text>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Counts on the left */}
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-2"
          data-testid="transactions-stats"
        >
          {KINDS.map((kind) => (
            <KindStatBox
              key={kind.key}
              kind={kind}
              totals={data.totals}
              today={data.today}
            />
          ))}
        </div>

        {/* 7-day trend chart on the right */}
        <div
          className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 lg:col-span-3"
          data-testid="transactions-trend-chart"
        >
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
            Last 7 days
          </p>
          <div className="h-72 w-full">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="currentColor"
                  className="text-gray-200 dark:text-gray-700"
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
                />
                <YAxis
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
                  tickFormatter={(value: number) => formatCompactNumber(Number(value))}
                />
                <Tooltip content={<TrendChartTooltip />} />
                {KINDS.map((kind) => (
                  <Line
                    key={kind.key}
                    type="monotone"
                    dataKey={kind.key}
                    name={kind.label}
                    stroke={kind.colour}
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </section>
  )
}
